import './styles/graphic.scss';
import * as d3 from 'd3';

const DATA_URL = 'https://ix.cnn.io/data/strait-of-hormuz-shipments/strait-of-hormuz-shipments.json';
const TICK_STEP = 50;
const DESIRED_TICK_COUNT = 10;
const Y_MAX = 200;
const LINE_STROKE_WIDTH = 1;
const X_AXIS_CLASS = 'x-axis';
const Y_AXIS_CLASS = 'y-axis grid';
const COLOURS = {
  y2025Avg: { stroke: '#737373' },
  y2026: { fill: '#F18851' },
};
const ROLLING_WINDOW = 7;
const CHARTS = [
  { key: 'crude_exports', title: 'Crude oil exports' },
  // { key: 'fert_exports', title: 'Fertilizer exports' },
  { key: 'lng_exports', title: 'Liquid Natural Gas exports' },
];

const parseDate = d3.timeParse('%Y-%m-%d');

let cachedJson = null;
let loadPromise = null;
let renderId = 0;

function addRollingAverage(rows, key, window = ROLLING_WINDOW) {
  return rows.map((d, i) => {
    const slice = rows.slice(Math.max(0, i - window + 1), i + 1);
    return {
      ...d,
      y2025Avg: d3.mean(slice, row => row[key]),
    };
  });
}

function parseSeries(rows) {
  return addRollingAverage(rows.map(d => ({
    date: parseDate(d.date),
    y2025: +d['2025'],
    y2026: +d['2026'],
  })), 'y2025');
}

function getEvenlySpacedTicks(min, max, count = DESIRED_TICK_COUNT, stepBase = TICK_STEP) {
  if (max <= min) return [min];
  const roundedMax = Math.ceil(max / stepBase) * stepBase;
  let step = (roundedMax - min) / (count - 1);
  step = Math.max(Math.round(step / stepBase) * stepBase, stepBase);
  const ticks = [];
  for (let i = 0; i < count; i++) {
    ticks.push(min + i * step);
  }
  ticks[0] = min;
  const cappedMax = Math.min(roundedMax, max);
  if (ticks[ticks.length - 1] < cappedMax) {
    ticks[ticks.length - 1] = cappedMax;
  }
  return ticks.filter(t => t <= max);
}

function updateAnnotationLabels({
  block,
  key,
  x,
  y,
  yMax,
  height,
  margins,
  isMobile,
}) {
  block.select('.strikes-label')
    .style('position', 'absolute')
    .style('transform', isMobile
      ? `translateX(${x(new Date('2026-02-28')) + margins.left - 5 - 100}px) translateY(${y(yMax) - height - margins.top - margins.bottom}px)`
      : `translateX(${x(new Date('2026-02-28')) + margins.left + 5}px) translateY(${y(yMax) - height - margins.top - margins.bottom}px)`)
    .html(key === 'crude_exports'
      ? '<div><span class="annotation-date">Feb. 28</span><br>US-Israeli strikes on Iran begin</div>'
      : '<span class="annotation-date">Feb. 28</span>');

  block.select('.mou-label')
    .style('position', 'absolute')
    .style('transform', `translateX(${x(new Date('2026-06-17')) + margins.left - 5 - 100}px) translateY(${y(yMax) - height - margins.top - margins.bottom}px)`)
    .html(key === 'crude_exports'
      ? '<span class="annotation-date">June 17</span><br>US and Iran sign agreement'
      : '<span class="annotation-date">June 17</span>');
}

function drawChart({
  container,
  key,
  data,
  title,
  initialWidth,
  plotHeight,
  margins,
  isMobile,
}) {
  const width = initialWidth - margins.left - margins.right;
  const height = plotHeight;
  const svgHeight = height + margins.top + margins.bottom;

  const block = container.select(`.chart-block[data-key="${key}"]`);
  block.select('.chart-title').text(title);
  block.selectAll('svg').remove();

  const svg = block.insert('svg', '.strikes-label')
    .attr('width', initialWidth)
    .attr('height', svgHeight);

  const chartG = svg.append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  chartG.append('g')
    .attr('class', X_AXIS_CLASS)
    .attr('transform', `translate(0,${height})`);

  chartG.append('g')
    .attr('class', Y_AXIS_CLASS);

  const x = d3.scaleTime()
    .domain(d3.extent(data, d => d.date))
    .range([0, width]);

  const yMax = Y_MAX;
  const y = d3.scaleLinear()
    .domain([0, yMax])
    .range([height, 0])
    .clamp(true);

  const yExtended = d3.scaleLinear()
    .domain([0, yMax])
    .range([height, 0]);

  chartG.append('defs')
    .append('clipPath')
    .attr('id', `clip-${key}`)
    .append('rect')
    .attr('width', width)
    .attr('height', height);

  const plotG = chartG.append('g')
    .attr('clip-path', `url(#clip-${key})`);

  const desiredXTicks = isMobile ? 5 : 6;
  const xTickInterval = Math.max(1, Math.ceil((data.length - 1) / (desiredXTicks - 1)));

  const xTicks = data
    .map(d => d.date)
    .filter((_, i) => i % xTickInterval === 0 || i === data.length - 1);

  const xAxis = d3.axisBottom(x)
    .tickValues(xTicks)
    .tickFormat(d => d3.timeFormat('%b %-d')(d));

  chartG.select('.x-axis').call(xAxis)
    .selectAll('text')
    .style('transform', 'translate(0, 5px)')
    .style('text-anchor', 'center');

  const yTicks = getEvenlySpacedTicks(0, yMax);
  const yAxis = d3.axisLeft(y)
    .tickValues(yTicks)
    .tickSize(-width);

  chartG.select('.y-axis').call(yAxis);
  chartG.select('.y-axis').selectAll('.tick line')
    .attr('stroke', '#ccc');
  chartG.select('.y-axis').selectAll('.tick text')
    .style('transform', 'translate(-5px, 0)');

  // chartG.select('.y-axis').select('.domain')
  //   .attr('display', 'none')
  //   .attr('stroke', 'none');

  const barWidth = data.length > 1
    ? x(data[1].date) - x(data[0].date)
    : width;

  chartG.append('line')
    .attr('class', 'annotation-line')
    .attr('x1', x(new Date('2026-02-28')))
    .attr('y1', y(0))
    .attr('x2', x(new Date('2026-02-28')))
    .attr('y2', yExtended(250))
    .attr('stroke', '#8E8E8E')
    .attr('stroke-width', 1);

  if (key === 'crude_exports') {
    chartG.append('text')
      .attr('class', 'annotation-label')
      .attr('x', x(new Date('2026-05-05')))
      .attr('y', y(120))
      .attr('text-anchor', 'middle')
      .attr('fill', COLOURS.y2025Avg.stroke)
      .text(isMobile ? '2025 7-day avg' : '2025 7-day rolling average');
  }

  chartG.append('line')
    .attr('class', 'annotation-line')
    .attr('x1', x(new Date('2026-06-17')))
    .attr('y1', y(0))
    .attr('x2', x(new Date('2026-06-17')))
    .attr('y2', yExtended(250))
    .attr('stroke', '#8E8E8E')
    .attr('stroke-width', 1);

  updateAnnotationLabels({
    block,
    key,
    x,
    y,
    yMax,
    height,
    margins,
    isMobile,
  });

  plotG.append('g')
    .attr('class', 'bars-2026')
    .selectAll('rect')
    .data(data)
    .join('rect')
    .attr('x', d => x(d.date) - barWidth / 2)
    .attr('y', d => y(d.y2026))
    .attr('width', barWidth)
    .attr('height', d => height - y(d.y2026))
    .attr('fill', COLOURS.y2026.fill);

  const line2025Avg = d3.line()
    .x(d => x(d.date))
    .y(d => y(d.y2025Avg));

  plotG.append('path')
    .attr('class', 'line-2025-avg')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', COLOURS.y2025Avg.stroke)
    .attr('stroke-width', LINE_STROKE_WIDTH)
    .attr('stroke-linecap', 'round')
    .attr('stroke-linejoin', 'round')
    .attr('d', line2025Avg);
}

export default function (props, container, isMobile) {
  const { initialWidth, initialHeight, margins } = props;
  const chartsContainer = d3.select('#charts');

  renderId += 1;
  const currentRenderId = renderId;

  const blockGap = isMobile ? 20 : 28;
  const titleHeight = 22;
  const chartCount = CHARTS.length;
  const plotHeight = Math.max(
    110,
    Math.floor((initialHeight - chartCount * titleHeight - (chartCount - 1) * blockGap) / chartCount)
      - margins.bottom,
  );

  const render = (json) => {
    if (currentRenderId !== renderId) return;

    chartsContainer.selectAll('svg').remove();

    const latestSeries = parseSeries(json[CHARTS[0].key]);
    const latestDateStr = d3.timeFormat('%B %-d, %Y')(latestSeries[latestSeries.length - 1].date);

    const noteHtml = `Data as of ${json.lastUpdatedStr || latestDateStr}.`;
    d3.select('#footer #dynamic-note').remove();
    d3.select('#footer')
      .insert('p', ':first-child')
      .attr('id', 'dynamic-note')
      .attr('class', 'source')
      .style('margin', '15px 0 0 0')
      .style('color', '#8C8C8C')
      .style('font-size', '12px')
      .html(noteHtml);

    CHARTS.forEach(({ key, title }) => {
      drawChart({
        container: chartsContainer,
        key,
        data: parseSeries(json[key]),
        title,
        initialWidth,
        plotHeight,
        margins,
        isMobile,
      });
    });

    if (window.pymChild) window.pymChild.sendHeight();
  };

  if (cachedJson) {
    render(cachedJson);
    return;
  }

  if (!loadPromise) {
    loadPromise = d3.json(DATA_URL)
      .then((json) => {
        cachedJson = json;
        return json;
      })
      .catch((error) => {
        loadPromise = null;
        throw error;
      });
  }

  loadPromise
    .then(render)
    .catch((error) => {
      if (currentRenderId !== renderId) return;
      console.error('Error loading or processing data:', error);
    });
}
